import { useTranslation } from '@mukil/i18n';
import { Badge, EmptyState, GlassCard } from '@jumpstone/ui-react';
import { FieldRow, NumericFieldRow } from '../../components/common/field_row';
import { format_date, format_money, format_month_year } from '../../lib/format';
import type { CurrencyCode, StatutoryDeadline } from '../../data/types';

/**
 * The next statutory remittance, with its amount, owner and filing channel.
 *
 * `deadline` is `null` when no unfiled EPF/ETF/APIT obligation exists in the
 * resolved filing-calendar window (C5, #21936, see
 * `wagepro_store_dashboard.ts`'s `deriveStatutoryDeadline`) — a real
 * "nothing due" state, rendered as an empty state rather than a stale mock
 * deadline.
 *
 * `deadline.kind === 'overdue'` gets the `danger` badge variant (same
 * semantic danger token every other WagePro danger surface uses -- e.g.
 * `ActionItem.tone === 'danger'`) plus the "Overdue by N days" label, and
 * the upcoming due date is shown on a second line so the operator sees
 * what's next after they clear the overdue backlog. Both label paths render
 * a NON-NEGATIVE day count -- the previous signed "Due in -31 days"
 * countdown (issue #24350) is not reachable from this component anymore.
 */
export function StatutoryDeadlineCard({
  deadline,
  currency,
}: {
  deadline: StatutoryDeadline | null;
  currency: CurrencyCode;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');

  if (deadline === null) {
    return (
      <GlassCard variant="strong" className="rounded-2xl p-5">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-[color:var(--accent)] mb-3">
          {t('dashboard.statutory.kicker')}
        </p>
        <EmptyState
          title={t('dashboard.statutory.none_title')}
          description={t('dashboard.statutory.none_description')}
        />
      </GlassCard>
    );
  }

  const is_overdue = deadline.kind === 'overdue';
  const is_due_today = !is_overdue && deadline.days_remaining === 0;
  const urgent = !is_overdue && deadline.days_remaining <= 7;
  const badge_variant = is_overdue
    ? 'danger'
    : is_due_today || urgent
      ? 'warning'
      : 'info';
  // Due today gets its own literal copy rather than an interpolated "Due in
  // 0 days" — reads as unambiguously today rather than "how many days" (a
  // NIT from the issue #24350 fixup adversarial review).
  const badge_label = is_overdue
    ? t('dashboard.statutory.overdue_by', { days: deadline.days_remaining })
    : is_due_today
      ? t('dashboard.statutory.due_today')
      : t('dashboard.statutory.due_in', { days: deadline.days_remaining });

  return (
    <GlassCard variant="strong" className="rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[11px] uppercase tracking-wider font-semibold text-[color:var(--accent)]">
            {t('dashboard.statutory.kicker')}
          </p>
          <h2 className="text-[15px] font-bold mt-1">
            {t('dashboard.statutory.title', {
              period: format_month_year(deadline.period, locale),
            })}
          </h2>
          {deadline.next !== null && (
            <p className="text-[12px] text-[color:var(--ink-muted)] mt-1">
              {t('dashboard.statutory.next', {
                period: format_month_year(deadline.next.period, locale),
                date: format_date(deadline.next.due_on, locale),
              })}
            </p>
          )}
        </div>
        <Badge variant={badge_variant}>{badge_label}</Badge>
      </div>

      <NumericFieldRow label={t('dashboard.statutory.amount_due')}>
        {deadline.amount_due === null
          ? '—'
          : format_money(deadline.amount_due, currency, locale)}
      </NumericFieldRow>
      <FieldRow label={t('dashboard.statutory.filed_via')}>
        {deadline.filed_via}
      </FieldRow>
      <FieldRow label={t('dashboard.statutory.owner')}>
        {deadline.owner}
      </FieldRow>
      <FieldRow label={t('dashboard.statutory.due_on')}>
        {format_date(deadline.due_on, locale)}
      </FieldRow>
    </GlassCard>
  );
}
